import { useMemo, useState } from 'react'
import { LanesBoard } from './LanesBoard'

type Lane = {
  id: string
  route: string
  equipment: string
  index: number
  range: [number, number]
  history: number[]
  forecast: number[]
  confidence: number
  volatility: 'Low' | 'Medium' | 'High'
  capacity: string
}

const LANES: Lane[] = [
  {
    id: 'sha-rtm',
    route: 'CNSHA → NLRTM',
    equipment: '40HC',
    index: 3660,
    range: [3410, 3980],
    history: [3260, 3330, 3290, 3410, 3520, 3460, 3660],
    forecast: [3690, 3740, 3810],
    confidence: 72,
    volatility: 'Medium',
    capacity: 'Tightening',
  },
  {
    id: 'ngb-nyc',
    route: 'CNNGB → USNYC',
    equipment: '40HC',
    index: 4930,
    range: [4650, 5320],
    history: [4510, 4580, 4720, 4630, 4810, 4880, 4930],
    forecast: [4890, 4820, 4780],
    confidence: 64,
    volatility: 'High',
    capacity: 'Balanced',
  },
  {
    id: 'sgn-fxt',
    route: 'VNSGN → GBFXT',
    equipment: '40HC',
    index: 3180,
    range: [3040, 3370],
    history: [2940, 3010, 2960, 3070, 3110, 3150, 3180],
    forecast: [3210, 3250, 3270],
    confidence: 77,
    volatility: 'Low',
    capacity: 'Stable',
  },
]

const money = (value: number) => `$${value.toLocaleString('en-US')}`

export function MarketIntelligence() {
  const [laneId, setLaneId] = useState(LANES[0]?.id ?? '')
  const lane = LANES.find((item) => item.id === laneId) ?? LANES[0]
  const [horizon, setHorizon] = useState<'30' | '60' | '90'>('90')
  if (!lane) return null
  const points = [...lane.history, ...lane.forecast.slice(0, Number(horizon) / 30)]
  const change = Math.round((((points.at(-1) ?? lane.index) - lane.index) / lane.index) * 100)

  return (
    <section className="fmi" aria-label="Market rate intelligence">
      <style>{styles}</style>
      <header className="fmi-head">
        <div>
          <span>Blended freight market index</span>
          <h2>Read the market. Price with context.</h2>
          <p>One lane view blended from carrier, partner, spot and observed market rates.</p>
        </div>
        <label>
          <small>Lane</small>
          <select
            aria-label="Choose market lane"
            value={laneId}
            onChange={(e) => setLaneId(e.target.value)}
          >
            {LANES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.route} · {item.equipment}
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="fmi-tape">
        <Metric label="Navo lane index" value={money(lane.index)} note="blended median · today" />
        <Metric
          label="Tradable range"
          value={`${money(lane.range[0])}–${money(lane.range[1])}`}
          note="middle 60% of live observations"
        />
        <Metric label="Volatility" value={lane.volatility} note="based on 8-week movement" />
        <Metric
          label="Capacity signal"
          value={lane.capacity}
          note="sailings, blankings and allocation"
        />
      </div>

      <div className="fmi-grid">
        <section className="fmi-chart-card">
          <header>
            <div>
              <span>History + outlook</span>
              <h3>{lane.route}</h3>
            </div>
            <div className="fmi-horizon" aria-label="Forecast horizon">
              {(['30', '60', '90'] as const).map((value) => (
                <button
                  type="button"
                  className={horizon === value ? 'active' : ''}
                  onClick={() => setHorizon(value)}
                  key={value}
                >
                  {value}d
                </button>
              ))}
            </div>
          </header>
          <ForecastChart
            history={lane.history}
            forecast={lane.forecast.slice(0, Number(horizon) / 30)}
          />
          <footer>
            <span>6 months ago</span>
            <span>Today</span>
            <span>Forecast</span>
          </footer>
        </section>

        <aside className="fmi-outlook">
          <span>Model outlook</span>
          <strong className={change < 0 ? 'down' : ''}>
            {change > 0 ? '+' : ''}
            {change}%
          </strong>
          <h3>
            {change > 1 ? 'Upward pressure' : change < -1 ? 'Softening likely' : 'Broadly stable'}
          </h3>
          <p>
            Expected movement over {horizon} days. Direction is more reliable than the exact price.
          </p>
          <div>
            <span>Confidence</span>
            <b>{lane.confidence}%</b>
          </div>
          <div className="fmi-confidence">
            <i style={{ width: `${lane.confidence}%` }} />
          </div>
          <button type="button">Use in client quote →</button>
        </aside>
      </div>

      <div className="fmi-lower">
        <section className="fmi-drivers">
          <header>
            <div>
              <span>What is moving this lane</span>
              <h3>Index drivers</h3>
            </div>
            <small>impact on rate</small>
          </header>
          <Driver
            tone="#e5556b"
            title="Capacity & blank sailings"
            detail="Available weekly slots have reduced across the next three departures."
            impact="+3.1%"
          />
          <Driver
            tone="#f59e0b"
            title="Fuel and bunker index"
            detail="Bunker component is slightly higher month-on-month."
            impact="+0.8%"
          />
          <Driver
            tone="#10b981"
            title="Demand & booking pace"
            detail="Forward booking pace remains inside the seasonal range."
            impact="−0.4%"
          />
          <Driver
            tone="#2563eb"
            title="Congestion & reliability"
            detail="Port dwell and schedule reliability are broadly stable."
            impact="Neutral"
          />
        </section>

        <section className="fmi-actions">
          <header>
            <span>Commercial signal</span>
            <h3>What to do now</h3>
          </header>
          <article>
            <b>Quote validity</b>
            <strong>7 days</strong>
            <p>Keep validity short while capacity tightens.</p>
          </article>
          <article>
            <b>Buying position</b>
            <strong>Fix 50–60%</strong>
            <p>Protect core volume; leave some exposure to the market.</p>
          </article>
          <article>
            <b>Target buy</b>
            <strong>{money(Math.round(lane.index * 0.97))}</strong>
            <p>A credible negotiation anchor from the current range.</p>
          </article>
          <button type="button">Create quote from signal →</button>
        </section>
      </div>

      <footer className="fmi-method">
        <b>How the index works</b>
        <span>
          Weighted median of comparable carrier contracts, partner offers, spot quotes and observed
          market rates. Stale and outlier observations are discounted. Forecasts are indicative—not
          bookable rates.
        </span>
        <button type="button">View methodology</button>
      </footer>

      <section className="fmi-live">
        <header>
          <div>
            <span>FreightRatesMCP live dashboard</span>
            <h2>Compare the rates behind the index.</h2>
            <p>
              Live liner cards, current sailings and container-specific prices in the same market
              workspace.
            </p>
          </div>
          <div className="fmi-live-key">
            <i />
            <span>Bookable observation</span>
            <i />
            <span>Indicative only</span>
          </div>
        </header>
        <LanesBoard heading={false} embedded />
      </section>
    </section>
  )
}

function Metric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  )
}

function Driver({
  tone,
  title,
  detail,
  impact,
}: { tone: string; title: string; detail: string; impact: string }) {
  return (
    <article>
      <i style={{ background: tone }} />
      <div>
        <b>{title}</b>
        <span>{detail}</span>
      </div>
      <strong>{impact}</strong>
    </article>
  )
}

function ForecastChart({ history, forecast }: { history: number[]; forecast: number[] }) {
  const { past, future, split } = useMemo(() => {
    const all = [...history, ...forecast]
    const min = Math.min(...all) * 0.97
    const max = Math.max(...all) * 1.03
    const y = (value: number) => 88 - ((value - min) / (max - min || 1)) * 70
    const x = (index: number) => (index / (all.length - 1)) * 100
    const past = history.map((value, index) => `${x(index)},${y(value)}`).join(' ')
    const future = [history.at(-1) ?? forecast[0] ?? 0, ...forecast]
      .map((value, index) => `${x(history.length - 1 + index)},${y(value)}`)
      .join(' ')
    return { past, future, split: x(history.length - 1) }
  }, [history, forecast])
  return (
    <svg
      className="fmi-chart"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label="Historical and forecast market index"
    >
      <line
        x1={split}
        x2={split}
        y1="8"
        y2="92"
        stroke="currentColor"
        opacity=".15"
        strokeDasharray="2 3"
      />
      <polyline
        points={past}
        fill="none"
        stroke="#e5556b"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
      <polyline
        points={future}
        fill="none"
        stroke="#e5556b"
        strokeWidth="2"
        strokeDasharray="5 4"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

const styles = `
.fmi{padding-top:24px}.fmi *{box-sizing:border-box}.fmi button,.fmi select{font:inherit}.fmi-head{display:flex;justify-content:space-between;align-items:end;gap:24px}.fmi-head>div>span,.fmi-chart-card header span,.fmi-outlook>span,.fmi-drivers header span,.fmi-actions header span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;letter-spacing:.13em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.fmi-head h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:27px;letter-spacing:-.028em;margin:6px 0 3px}.fmi-head p{margin:0;color:var(--text-muted);font-size:13px}.fmi-head label{display:grid;gap:5px;min-width:270px}.fmi-head label small{font-family:var(--mono,ui-monospace,monospace);font-size:8px;text-transform:uppercase;color:var(--text-muted)}.fmi-head select{border:1px solid var(--border);border-radius:10px;background:var(--surface-0);color:inherit;padding:10px 12px;font-weight:650}.fmi-tape{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid var(--border);border-radius:14px;background:var(--surface-0);margin-top:18px;overflow:hidden;box-shadow:var(--shadow-card)}.fmi-tape>div{padding:15px 17px;border-right:1px solid var(--border)}.fmi-tape>div:last-child{border:0}.fmi-tape span,.fmi-tape small{display:block;color:var(--text-muted);font-size:9px}.fmi-tape strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:17px;margin:5px 0 2px}.fmi-grid{display:grid;grid-template-columns:minmax(0,2fr) minmax(230px,.72fr);gap:12px;margin-top:12px}.fmi-chart-card,.fmi-outlook,.fmi-drivers,.fmi-actions{border:1px solid var(--border);border-radius:14px;background:var(--surface-0);box-shadow:var(--shadow-card)}.fmi-chart-card{padding:16px 18px}.fmi-chart-card>header,.fmi-drivers>header{display:flex;justify-content:space-between;align-items:start}.fmi-chart-card h3,.fmi-drivers h3,.fmi-actions h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:19px;margin:3px 0 0}.fmi-horizon{display:flex;border:1px solid var(--border);border-radius:8px;overflow:hidden}.fmi-horizon button{border:0;border-right:1px solid var(--border);background:transparent;color:var(--text-muted);padding:6px 9px;font-size:9px}.fmi-horizon button:last-child{border:0}.fmi-horizon button.active{background:var(--text-primary);color:var(--surface-0)}.fmi-chart{width:100%;height:170px;margin-top:8px;overflow:visible}.fmi-chart-card>footer{display:grid;grid-template-columns:1fr 1fr 1fr;color:var(--text-muted);font-family:var(--mono,ui-monospace,monospace);font-size:8px}.fmi-chart-card>footer span:nth-child(2){text-align:center}.fmi-chart-card>footer span:last-child{text-align:right;color:var(--frd-accent)}.fmi-outlook{padding:17px;display:flex;flex-direction:column}.fmi-outlook>strong{font-family:var(--mono,ui-monospace,monospace);font-size:36px;color:var(--frd-accent);margin-top:8px}.fmi-outlook>strong.down{color:#047857}.fmi-outlook h3{font-size:14px;margin:0}.fmi-outlook p{font-size:10px;line-height:1.5;color:var(--text-muted);margin:5px 0 16px}.fmi-outlook>div:not(.fmi-confidence){display:flex;justify-content:space-between;font-size:9px}.fmi-confidence{height:5px;border-radius:5px;background:var(--surface-1);margin-top:6px;overflow:hidden}.fmi-confidence i{display:block;height:100%;background:var(--frd-accent)}.fmi-outlook button,.fmi-actions>button{margin-top:auto;border:0;border-radius:9px;background:var(--text-primary);color:var(--surface-0);padding:10px 12px;font-size:10px;font-weight:700}.fmi-lower{display:grid;grid-template-columns:1.55fr .75fr;gap:12px;margin-top:12px}.fmi-drivers{overflow:hidden}.fmi-drivers>header{padding:14px 16px;border-bottom:1px solid var(--border)}.fmi-drivers header small{font-size:8px;color:var(--text-muted)}.fmi-drivers article{display:grid;grid-template-columns:3px minmax(0,1fr) auto;gap:11px;align-items:center;padding:11px 15px;border-bottom:1px solid var(--border)}.fmi-drivers article:last-child{border:0}.fmi-drivers article i{width:3px;height:28px;border-radius:3px}.fmi-drivers article b,.fmi-drivers article span{display:block}.fmi-drivers article b{font-size:10px}.fmi-drivers article span{font-size:8.5px;color:var(--text-muted);margin-top:2px}.fmi-drivers article>strong{font-family:var(--mono,ui-monospace,monospace);font-size:11px}.fmi-actions{padding:15px}.fmi-actions article{border-top:1px solid var(--border);padding:10px 0}.fmi-actions article b{font-size:9px;color:var(--text-muted)}.fmi-actions article strong{float:right;font-family:var(--mono,ui-monospace,monospace);font-size:11px}.fmi-actions article p{font-size:8.5px;color:var(--text-muted);margin:3px 0 0}.fmi-actions>button{width:100%;margin-top:4px}.fmi-method{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:12px;align-items:center;margin-top:12px;padding:12px 15px;border-top:1px solid var(--border);font-size:8.5px;color:var(--text-muted)}.fmi-method b{color:var(--text-primary)}.fmi-method button{border:0;background:none;color:var(--frd-accent);font-size:9px;font-weight:700}.fmi-method button,.fmi-outlook button,.fmi-actions button,.fmi-horizon button{cursor:pointer}
.fmi-live{margin-top:34px;padding-top:25px;border-top:1px solid var(--border)}.fmi-live>header{display:flex;justify-content:space-between;align-items:end;gap:24px;margin-bottom:4px}.fmi-live>header>div:first-child>span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;letter-spacing:.13em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.fmi-live>header h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:25px;letter-spacing:-.025em;margin:5px 0 2px}.fmi-live>header p{font-size:11px;color:var(--text-muted);margin:0}.fmi-live-key{display:flex;align-items:center;gap:6px;font-size:8px;color:var(--text-muted);white-space:nowrap}.fmi-live-key i{width:7px;height:7px;border-radius:50%;background:#10b981}.fmi-live-key i:nth-of-type(2){background:#f59e0b;margin-left:7px}
@media(max-width:760px){.fmi-head{align-items:stretch;flex-direction:column}.fmi-head label{min-width:0}.fmi-tape{grid-template-columns:1fr 1fr}.fmi-tape>div{border-bottom:1px solid var(--border)}.fmi-grid,.fmi-lower{grid-template-columns:1fr}.fmi-chart{height:130px}.fmi-outlook button{margin-top:16px}.fmi-method{grid-template-columns:1fr}.fmi-method button{padding:0;text-align:left}.fmi-live>header{align-items:flex-start;flex-direction:column}.fmi-live-key{white-space:normal}}
`
